import { Injectable } from '@angular/core';
import { Animal } from '../../animal';
import { AnimalEvent } from '../animal-event';
import { Childbirth } from './childbirth';
import { Filter, FilterOp, NetworkStatusService, SortMode, SortOrder } from 'libs/arch/src/lib/repositories';
import {
    BaseIndexedDbRepository,
    SynchronizationService
} from 'libs/arch/src/lib/repositories/indexed-db';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { map, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ApiService } from '@homeleite/arch';

@Injectable({ providedIn: 'root' })
export class ChildbirthRepository extends BaseIndexedDbRepository<Childbirth, Childbirth> {
    protected override readonly storeName: string = 'childbirths';

    constructor(
        dbService: NgxIndexedDBService,
        synchronizationService: SynchronizationService,
        private networkStatus: NetworkStatusService,
        private apiService: ApiService
    ) {
        super(dbService, synchronizationService);

        this.registerAnimalReferences('animalId');
    }

    public override insert(childbirth: Childbirth): Observable<Childbirth> {
        return super.insert(childbirth).pipe(
            tap((savedChildbirth) => {
                const transformedEvent: AnimalEvent = {
                    id: savedChildbirth.id,
                    date: savedChildbirth.date,
                    animal: savedChildbirth.animal,
                    type: 'CHILDBIRTH',
                    created: savedChildbirth.created
                };

                this.dbService.add('animalEvents', transformedEvent).subscribe({
                    error: (err) => {
                        console.error('Falha ao agregar evento de Parto em animalEvents:', err);
                    }
                });
            })
        );
    }

    protected override mapToEntity(dbItem: Childbirth): Childbirth {
        return Childbirth.fromJson(dbItem);
    }

    protected override mapToSummary(item: Childbirth): Childbirth {
        return item;
    }

    protected override getSearchableFields(): string[] {
        return ['animal.name', 'animal.register', 'date', 'type', 'gender', 'animal.id'];
    }

    protected override getResourcePath(): string {
        return 'childBirths';
    }

    public findByAnimal(animal: Animal): Observable<Childbirth[]> {
        const animalId: string | undefined = animal?.id;
        if (!animalId) {
            return of([]);
        }

        const getFromDb = () => {
            return this.executeIndexedDbQuery({
                filters: [new Filter('animal.id', FilterOp.EQ, animalId)],
                sortOrders: [
                    new SortOrder('date', SortMode.DSC),
                    new SortOrder('auditData.created', SortMode.DSC)
                ]
            });
        };

        if (!this.networkStatus.isOnline()) {
            return getFromDb();
        }

        return this.fetchChildbirthsBy(animalId)
            .pipe(catchError(() => getFromDb()));
    }

    private fetchChildbirthsBy(animalId: string): Observable<Childbirth[]> {
        return this.apiService
            .root.path(this.getResourcePath())
            .path('animal')
            .path(animalId)
            .getJson<any[]>()
            .pipe(
                map((response) =>
                    Array.isArray(response) ? response.map((item) => Childbirth.fromJson(item)) : []
                ),
                map((childbirths) =>
                    childbirths.sort((a, b) => {
                        const getTs = (v: any) => new Date(v ?? 0).getTime();
                        const diff = getTs(b.date) - getTs(a.date);
                        if (diff !== 0) return diff;
                        return getTs(b.auditData?.created) - getTs(a.auditData?.created);
                    })
                ),
                tap((childbirths) => {
                    if (!childbirths?.length) return;

                    const integrationChildbirths = childbirths.filter((childbirth) => {
                        const { auditData } = childbirth;
                        return (
                            auditData?.createdByIntegration || (auditData?.createdByApp && auditData?.modifiedByIntegration)
                        );
                    });

                    if (integrationChildbirths.length > 0) {
                        this.saveLocally(integrationChildbirths).subscribe({
                            error: (err) => console.error('Falha ao salvar partos em lote:', err)
                        });
                    }
                })
            );
    }
}
